import type { TFunction } from 'i18next';
import type { ReplyOwnership } from '../api/tickets';

/**
 * What to tell the sender after a SUCCESSFUL human send, from the server's
 * ownership decision (dashboard services/replyOwnership.ts). Every reply surface
 * — the ticket composer (text, media, voice) and the outbound dialog — goes
 * through this one function, so "the message was sent" is never conflated with
 * "you now own this customer":
 *   • transferred                → the sender is the owner now;
 *   • out_of_scope / superseded  → sent, ownership NOT transferred, and why;
 *   • failed                     → sent, owner update unresolved (no resend);
 *   • already_owner / none       → nothing changed, nothing to say.
 */
export function ownershipNotice(
  t: TFunction,
  ownership: ReplyOwnership | undefined,
): { message: string; transferred: boolean } | null {
  switch (ownership?.outcome) {
    case 'transferred':
      return { message: t('ticket.ownership_taken'), transferred: true };
    case 'out_of_scope':
      return { message: t('ticket.ownership_out_of_scope'), transferred: false };
    case 'superseded_by_newer_reply':
    case 'superseded_by_assignment':
      return { message: t('ticket.ownership_superseded'), transferred: false };
    case 'failed':
      return { message: t('ticket.ownership_failed'), transferred: false };
    default:
      return null;
  }
}
